/**
 * Responsibility: render one drop signal with the evidence behind it.
 *
 * The evidence is not an optional detail. A restock alert is self-evidently
 * true - the item is in the cart or it is not - but a rumour is a claim, and
 * showing which outlets said it is what lets the operator judge rather than
 * trust. The matched phrases are shown for the same reason: a false positive
 * should be diagnosable at a glance instead of mysterious.
 */
import { Box, Chip, Link, Stack, Typography } from '@mui/material';
import type { DropSignal } from '../../api/client';

type Strength = { label: string; color: 'success' | 'warning' | 'default' };

const RUMOUR: Strength = { label: 'Rumour', color: 'default' };

const STRENGTH: Record<string, Strength> = {
  CONFIRMED: { label: 'Confirmed', color: 'success' },
  CORROBORATED: { label: 'Corroborated', color: 'warning' },
  RUMOUR,
};

const TIER: Record<string, string> = {
  OFFICIAL: 'official',
  ESTABLISHED: 'outlet',
  COMMUNITY: 'community',
};

function when(value: string | null): string {
  if (!value) return '';
  return new Date(value).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

export function DropSignalCard({ signal }: { signal: DropSignal }) {
  const strength = STRENGTH[signal.strength] ?? RUMOUR;
  const notified = signal.notified_at !== null;

  return (
    <Box
      sx={{
        p: 2,
        borderRadius: 2,
        border: '1px solid',
        borderColor: signal.strength === 'RUMOUR' ? 'divider' : 'warning.main',
      }}
    >
      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
        <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
          {signal.product_name}
        </Typography>
        <Chip size="small" color={strength.color} variant="outlined" label={strength.label} />
        <Chip
          size="small"
          variant="outlined"
          label={`${signal.distinct_sources} source${signal.distinct_sources === 1 ? '' : 's'}`}
        />
        <Chip size="small" variant="outlined" label={`score ${signal.score}`} />
        {notified ? <Chip size="small" variant="outlined" label="notified" /> : null}
      </Stack>

      <Stack spacing={0.75} sx={{ mt: 1.5 }}>
        {signal.mentions.map((mention) => (
          <Box key={`${mention.source_key}-${mention.url}`}>
            <Link
              href={mention.url}
              target="_blank"
              rel="noopener noreferrer"
              variant="body2"
              underline="hover"
              // A thumb-sized target on a phone; desktop density is untouched.
              sx={{ minHeight: { xs: 44, md: 'auto' }, display: 'inline-flex', alignItems: 'center' }}
            >
              {mention.title}
            </Link>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
              {mention.source_name} ({TIER[mention.tier] ?? mention.tier})
              {mention.published_at ? ` - ${when(mention.published_at)}` : ''}
              {mention.matched_terms.length > 0
                ? ` - matched "${mention.matched_terms.join('", "')}"`
                : ''}
            </Typography>
          </Box>
        ))}
      </Stack>
    </Box>
  );
}
